import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiClientError } from "@/application/errors/ApiClientError";
import { ContractViolationError } from "@/application/errors/ContractViolationError";
import { getLogger } from "@/infrastructure/di/services";
import { addBreadcrumb, captureException } from "@/infrastructure/monitoring";
import { reloadOnContractViolationSkew } from "@/infrastructure/versionSkew";

/**
 * Central Sentry reporting for server-state errors. Repositories throw typed
 * errors (ApiClientError / ContractViolationError) and every query/mutation
 * funnels through these caches, so this is the single reporting point for
 * API failures — request-level breadcrumbs live here, not in the SDK client.
 */
function reportApiError(error: unknown, context: Record<string, unknown>): void {
  if (error instanceof ContractViolationError) {
    addBreadcrumb({
      category: "api",
      message: error.message,
      level: "error",
      data: { ...context, issues: error.zodError.issues.length },
    });
    captureException(error, {
      fingerprint: ["contract-violation", error.endpoint ?? "unknown"],
      tags: {
        "api.endpoint": error.endpoint ?? "unknown",
        "api.method": error.method ?? "unknown",
      },
      extra: { ...context, issues: error.zodError.issues },
    });
    // If the bundle is stale, this violation is version skew — reload into
    // the deployed bundle instead of leaving the user on broken code.
    void reloadOnContractViolationSkew();
    return;
  }

  if (error instanceof ApiClientError) {
    addBreadcrumb({
      category: "api",
      message: `${error.method ?? ""} ${error.endpoint ?? ""} - ${error.status} FAILED`.trim(),
      level: "error",
      data: { ...context, status: error.status, code: error.code },
    });
    // A 2xx that still surfaced as an error never made it past the client —
    // it breaks a screen while looking like success. 4xx stay out as noise.
    const succeededButFailed = error.status >= 200 && error.status < 300;
    const shouldReport =
      error.status >= 500 || error.status === 404 || error.status === 0 || succeededButFailed;
    if (shouldReport) {
      captureException(error, {
        fingerprint: ["api-error", error.endpoint ?? "unknown", String(error.status)],
        tags: {
          "api.endpoint": error.endpoint ?? "unknown",
          "api.method": error.method ?? "unknown",
          "api.status": String(error.status),
          ...(error.traceId ? { "backend.trace_id": error.traceId } : {}),
          ...(error.requestId ? { "backend.request_id": error.requestId } : {}),
        },
        extra: { ...context, errorDetails: error.details },
      });
    }
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      getLogger().error("[Query Error]", {
        error,
        queryKey: query.queryKey,
      });
      reportApiError(error, { kind: "query", queryKey: query.queryKey });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      getLogger().error("[Mutation Error]", {
        error,
        mutationKey: mutation.options.mutationKey,
      });
      reportApiError(error, { kind: "mutation", mutationKey: mutation.options.mutationKey });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error) => {
        // Don't retry on 4xx client errors (except 408 Request Timeout and 429 Too Many Requests)
        if (error && "status" in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            return false;
          }
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
  },
});
